import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IDeleteBucketRequest {
  bucketId: string;
}

interface IDeleteBucketResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
  };

  errors?: IApiResponseError[];
}

const initialState: IApiState<IDeleteBucketResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const deleteBucketAsync = createAsyncThunk(
  'bucket/delete',
  async ({ bucketId }: IDeleteBucketRequest, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.delete<IDeleteBucketResponse>(
        `/savings/accounts/delete-account/${bucketId}`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IDeleteBucketResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketDeletionSlice = createSlice({
  name: 'bucket/delete',
  initialState,
  reducers: {
    resetBucketDeletion(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteBucketAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(deleteBucketAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IDeleteBucketResponse;
      })
      .addCase(deleteBucketAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IDeleteBucketResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetBucketDeletion } = bucketDeletionSlice.actions;

export default bucketDeletionSlice.reducer;
